import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { 
  CreditCard, 
  QrCode, 
  Users, 
  BarChart3, 
  Scan, 
  Globe,
  Zap,
  Shield,
  Smartphone,
  ArrowRight,
  Check,
  Sparkles,
  RefreshCw,
  Leaf
} from 'lucide-react';

const Features = () => {
  const navigate = useNavigate();

  const mainFeatures = [
    {
      icon: CreditCard,
      title: 'NFC Smart Cards',
      description: 'Premium NFC-enabled business cards that share your profile with a simple tap. Works with all modern smartphones.',
      benefits: ['Instant contact sharing', 'No app required', 'Works with iPhone & Android', 'Durable materials'],
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: Globe,
      title: 'Digital Bio Pages',
      description: 'Beautiful, customizable profile pages that showcase your professional identity with all your links in one place.',
      benefits: ['Custom themes & colors', 'Unlimited links', 'Social media integration', 'Mobile optimized'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Users,
      title: 'Built-in CRM',
      description: 'Track and manage your contacts with our integrated CRM. Never lose a lead again with automatic follow-up reminders.',
      benefits: ['Lead pipeline management', 'Contact organization', 'Deal tracking', 'Team collaboration'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Understand your networking impact with detailed analytics on profile views, link clicks, and engagement patterns.',
      benefits: ['Real-time tracking', 'View statistics', 'Link performance', 'Export reports'],
      color: 'from-green-500 to-emerald-500'
    }
  ];

  const additionalFeatures = [
    { icon: QrCode, title: 'QR Code Generator', description: '15+ QR code types for any use case' },
    { icon: Scan, title: 'Card Scanner', description: 'Digitize any business card with AI-powered OCR' },
    { icon: RefreshCw, title: 'Instant Updates', description: 'Change your info anytime, everywhere' },
    { icon: Shield, title: 'Privacy Controls', description: 'Choose what information to share' },
    { icon: Smartphone, title: 'Mobile App', description: 'Manage everything from your phone' },
    { icon: Leaf, title: 'Sustainable Choice', description: 'One smart card for unlimited connections' },
  ];

  return (
    <Layout>
      <SEO 
        title="Features | Piper Card - Complete Digital Business Card Platform"
        description="Discover all Piper Card features: NFC smart cards, digital bio pages, built-in CRM, analytics dashboard, QR code generator, and more."
        keywords={['NFC business card features', 'digital business card platform', 'business card CRM', 'networking analytics']}
      />

      <div className="min-h-screen">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              All-in-One Platform
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Everything You Need for
              <br />
              <span className="text-amber-400">Modern Networking</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-10">
              Piper Card combines physical NFC cards with powerful digital tools to create 
              the ultimate professional networking ecosystem.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-amber-500 hover:bg-amber-600 rounded-full px-8"
                onClick={() => navigate('/shop')}
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-gray-600 text-white hover:bg-gray-800 rounded-full px-8"
                onClick={() => navigate('/pricing')}
              >
                View Pricing
              </Button>
            </div>
          </div>
        </div>

        {/* Main Features */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Core Platform Features
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Four powerful pillars that make Piper Card the complete networking solution
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {mainFeatures.map((feature, index) => (
                <Card key={index} className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-shadow duration-500">
                  <CardContent className="p-8">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 shadow-lg`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {feature.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Additional Features */}
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                And Much More...
              </h2>
              <p className="text-xl text-gray-600">
                Additional tools to supercharge your networking
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {additionalFeatures.map((feature, index) => (
                <Card key={index} className="border-0 bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Experience All Features?
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Start free and upgrade when you need more
            </p>
            <Button 
              size="lg" 
              className="bg-white text-amber-600 hover:bg-gray-100 rounded-full px-10 py-6 text-lg shadow-xl"
              onClick={() => navigate('/create')}
            >
              Create Free Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Features;
